import * as React from "react"
import { useEffect, useLayoutEffect } from "react"
import { useHistory } from "react-router"
import { Flex, Spinner } from "@indeed/ifl-components"
import { useQueryParams } from "../../common/hooks/hooks"
import { configSelector } from "../../common/utils/config-slice"
import { logDatadogRumMessage } from "../../common/utils/datadog"
import { useI18n } from "../../common/utils/i18n-context"
import { useAppDispatch, useAppSelector } from "../../common/utils/redux-helpers"
import { displayFooter, parseEssentialQueryParams } from "../../common/utils/utils"
import { ErrorPage } from "../error-page"
import { PauseAlreadyPaused } from "./pause-already-paused"
import { getPauseStatus, submitPause } from "./pause-api"
import { PauseHeader } from "./pause-header"
import { PausePageStatus, PauseSubmissionStatus } from "./pause-model"
import { pausePageStatusSelector, pauseStatusSelector, pauseSlice } from "./pause-slice"
import { PauseSubHeader } from "./pause-subheader"
import { PauseTimePicker } from "./pause-time-picker"

export const PausePage: React.FC = () => {
    const config = useAppSelector(configSelector)
    const history = useHistory()
    const { info: encryptedInfo, from } = parseEssentialQueryParams(useQueryParams())
    const { isPaused } = useAppSelector(pauseStatusSelector)
    const pageStatus = useAppSelector(pausePageStatusSelector)
    const dispatch = useAppDispatch()
    const isPauseStatusLoaded =
        pageStatus === PausePageStatus.PAUSE_STATUS_LOADED || pageStatus === PausePageStatus.PAUSE_STATUS_LOADED_SUBMIT_ERROR
    const isAlreadyPaused = isPauseStatusLoaded && isPaused
    const { gettext } = useI18n()

    if (!encryptedInfo) {
        logDatadogRumMessage("No info object present in queryParams")
        history.push(config.errorUrl)
        return null
    }

    useEffect(() => {
        dispatch(getPauseStatus({ from }))
    }, [])

    useEffect(() => {
        if (isPauseStatusLoaded) {
            if (isPaused) {
                // Skip submitPause() if user is already paused
                // PauseSubmissionStatus is default as 'SUBMITTING'.  Switch to 'READY' to ensure proper button state.
                dispatch(pauseSlice.actions.updatePauseSubmissionStatus(PauseSubmissionStatus.READY))
            } else {
                dispatch(
                    submitPause({
                        encryptedInfo,
                        from,
                        initialPause: true,
                        gettext,
                    })
                )
            }
        }
    }, [isPauseStatusLoaded, isPaused])

    useLayoutEffect(() => {
        displayFooter()
    }, [])

    if (pageStatus === PausePageStatus.INITIAL_PAUSE_ERROR || pageStatus === PausePageStatus.PAUSE_STATUS_ERROR) {
        return (
            <ErrorPage
                subHeader={gettext("Please refresh the page to try again.")}
                pageStylingOverride={{ blockSize: "100%", justifyContent: "space-between" }}
                hidePreferencesButton
            />
        )
    }

    if (pageStatus === PausePageStatus.LOADING_PAUSE_STATUS || pageStatus === PausePageStatus.SUBMITTING_INITIAL_PAUSE) {
        return (
            <PausePageContainer>
                <Spinner title={gettext("loading")} inverse />
            </PausePageContainer>
        )
    }

    return (
        <PausePageContainer>
            <Flex
                sx={{
                    gap: 2,
                    flexDirection: "column",
                    inlineSize: "100%",
                }}
            >
                {isAlreadyPaused && <PauseAlreadyPaused />}
                {(pageStatus === PausePageStatus.PAUSE_SUCCESS || pageStatus === PausePageStatus.UNPAUSE_SUCCESS) && (
                    <>
                        <PauseHeader />
                        <PauseSubHeader />
                    </>
                )}
                <PauseTimePicker encryptedInfo={encryptedInfo} />
            </Flex>
        </PausePageContainer>
    )
}

interface PausePageContainerProps {
    children: React.ReactNode
}

const PausePageContainer: React.FC<PausePageContainerProps> = ({ children }) => (
    <Flex
        sx={{
            inlineSize: ["100%", "35.625rem"],
            marginBlockEnd: 0,
            marginBlockStart: 0,
            marginInlineEnd: "auto",
            marginInlineStart: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBlockEnd: 4,
            paddingBlockStart: 4,
            paddingInlineEnd: 4,
            paddingInlineStart: 4,
        }}
    >
        {children}
    </Flex>
)
